import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import type { Container } from "@shared/types/supabase.types";
import { Card, CardContent } from "@/components/ui/card";
import { ProfileAvatar } from "@/features/avatar";
import { LOGO_STORAGE_BUCKET } from "@/features/containers/components/container-setup-form";
import { cn } from "@/lib/utils";
import { getInitials } from "@/features/avatar/avatar.utils";

export type ContainerGridProps = {
  items: Container[];
  emptyState?: ReactNode;
  className?: string;
  gridClassName?: string;
  onItemClick?: (item: Container) => void;
  maxContainers?: number;
  seeMoreHref?: string;
};

function ContainerGridItemCard({
  item,
  onClick,
}: {
  item: Container;
  onClick?: (item: Container) => void;
}) {
  const title = item.name || item.slug || "";
  const isClickable = !!onClick;

  return (
    <Card
      className={cn(
        "relative transition hover:shadow-sm",
        isClickable && "hover:border-primary/60 cursor-pointer",
      )}
      onClick={() => onClick?.(item)}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <ProfileAvatar
          avatarPath={item.logo_url || null}
          size={50}
          storageName={LOGO_STORAGE_BUCKET}
          fallbackInitials={getInitials({
            workspaceName: item.name,
            avatarFallbackName: "W",
          })}
          className="rounded-lg"
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{title}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function ContainerGrid({
  items,
  emptyState,
  className,
  gridClassName = "grid gap-4 md:grid-cols-2 lg:grid-cols-4",
  onItemClick,
  maxContainers,
  seeMoreHref,
}: ContainerGridProps) {
  const hasItems = items.length > 0;

  const visibleItems = maxContainers ? items.slice(0, maxContainers) : items;

  if (!hasItems && emptyState) {
    return (
      <Card className={cn("border-dashed", className)}>
        <CardContent className="flex min-h-50 flex-col items-center justify-center px-0 py-0 text-center">
          {emptyState}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn(gridClassName, className)}>
      {visibleItems.map((item) => (
        <ContainerGridItemCard key={item.id} item={item} onClick={onItemClick} />
      ))}

      {!!maxContainers && (
        <a href={seeMoreHref} className="block">
          <Card className="hover:border-primary/60 group relative h-full transition hover:shadow-sm">
            <CardContent className="flex h-full min-h-20.5 items-center justify-center p-4">
              <span className="flex items-center gap-2 font-medium">
                See all projects
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </span>
            </CardContent>
          </Card>
        </a>
      )}
    </div>
  );
}
